import { useCallback, useEffect, useState } from 'react'
import {
  agentLabel,
  AUTHORITY_DESCRIPTION,
  AUTHORITY_LABEL,
  Authority,
  type Invite,
  type Member,
  type MemberSettings,
  type Role,
  type RoomState,
} from '@cs/shared'
import { api } from '../api.js'
import { describeError } from '../App.js'

const ROLE_LABEL: Record<Role, string> = {
  owner: 'オーナー',
  member: 'メンバー',
  viewer: '閲覧者',
}

const ROLE_HELP: Record<Role, string> = {
  owner: '招待・メンバー管理ができる',
  member: '発言・仕様書の提案・要約の共有ができる',
  viewer: '閲覧と発言のみ',
}

const ALL_AUTHORITIES = Authority.options

/**
 * Member administration.
 *
 * The two columns are deliberately separate. **権限** is what the system allows;
 * **責任領域** is what you are the decider for. They are not a single ladder: the
 * developer has the last word on implementation and the sales engineer has it on
 * requirements, and neither is senior to the other.
 */
export function Admin({
  room,
  me,
  state,
  onChanged,
}: {
  room: string
  me: Member
  state: RoomState | null
  onChanged: () => void
}) {
  const [members, setMembers] = useState<Member[]>([])
  const [invites, setInvites] = useState<Invite[]>([])
  const [error, setError] = useState<string | null>(null)
  const [info, setInfo] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const isOwner = me.role === 'owner'

  const load = useCallback(async () => {
    try {
      const [m, i] = await Promise.all([
        api.members(room),
        isOwner ? api.invites(room) : Promise.resolve({ invites: [] as Invite[] }),
      ])
      setMembers(m.members)
      setInvites(i.invites)
    } catch (err) {
      setError(describeError(err))
    }
  }, [room, isOwner])

  useEffect(() => {
    void load()
  }, [load])

  const act = async (fn: () => Promise<unknown>, message?: string) => {
    setBusy(true)
    setError(null)
    setInfo(null)
    try {
      await fn()
      if (message) setInfo(message)
      await load()
      onChanged()
    } catch (err) {
      setError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  const gaps = state?.authority_gaps ?? []

  return (
    <>
      {error && <div className="error">{error}</div>}
      {info && <div className="notice">{info}</div>}

      <MySettings
        room={room}
        me={members.find((m) => m.user_id === me.user_id) ?? me}
        busy={busy}
        onSave={(patch) => act(() => api.updateMySettings(room, patch), '設定を保存しました')}
      />

      {gaps.length > 0 && (
        <div className="notice">
          <strong>責任者が決まっていない領域があります:</strong>{' '}
          {gaps.map((g) => `${AUTHORITY_LABEL[g.domain]}${g.open_question_count > 0 ? `(質問${g.open_question_count}件が宙に浮いています)` : ''}`).join(' / ')}
          <br />
          その領域の質問は誰にも届きません。下の表で担当を設定してください。
        </div>
      )}

      {isOwner && <RenameCard room={room} current={state?.room.name ?? ''} onDone={onChanged} />}

      <div className="card">
        <h2>メンバー {members.length} 人</h2>
        <p className="muted" style={{ marginTop: 0 }}>
          <strong>権限</strong>はシステム上できることの範囲、<strong>責任領域</strong>は
          その人が最終判断をする領域です。両者は上下関係ではありません
          （実装の決定者と要件の決定者はどちらが上でもない）。
        </p>
        {members.map((m) => (
          <MemberRow
            key={m.user_id}
            member={m}
            isSelf={m.user_id === me.user_id}
            canEdit={isOwner}
            busy={busy}
            onSave={(patch) =>
              act(() => api.updateMember(room, m.user_id, patch), `${m.display_name} を更新しました`)
            }
            onRemove={() =>
              act(async () => {
                const res = await api.removeMember(room, m.user_id)
                setInfo(res.message)
              })
            }
          />
        ))}
      </div>

      {isOwner && (
        <InviteCard
          room={room}
          invites={invites}
          busy={busy}
          onCreate={(input) => act(() => api.createInvite(room, input))}
          onRevoke={(code) => act(() => api.revokeInvite(room, code), '招待を取り消しました')}
        />
      )}
    </>
  )
}

function MemberRow({
  member,
  isSelf,
  canEdit,
  busy,
  onSave,
  onRemove,
}: {
  member: Member
  isSelf: boolean
  canEdit: boolean
  busy: boolean
  onSave: (patch: { role?: Role; authorities?: Authority[] }) => void
  onRemove: () => void
}) {
  const [role, setRole] = useState<Role>(member.role)
  const [authorities, setAuthorities] = useState<Authority[]>(member.authorities)
  const [confirming, setConfirming] = useState(false)

  useEffect(() => {
    setRole(member.role)
    setAuthorities(member.authorities)
  }, [member])

  const dirty =
    role !== member.role ||
    JSON.stringify([...authorities].sort()) !== JSON.stringify([...member.authorities].sort())

  const toggle = (a: Authority) =>
    setAuthorities((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]))

  return (
    <div className="card" style={{ marginBottom: 10 }}>
      <div className="row">
        <strong>{member.display_name}</strong>
        <span className="muted">{member.org}</span>
        {isSelf && <span className="pill human">あなた</span>}
        {/* Whether their Claude can pick something up right now — which is what
            decides between a fast answer and one that waits until morning. */}
        <span className={`pill ${member.agent_online ? 'online' : 'offline'}`}>
          {member.agent_nickname ? `${member.agent_nickname}: ` : 'AI: '}
          {member.agent_online ? 'オンライン' : 'オフライン'}
        </span>
        {!member.agent_online && member.agent_last_seen_at && (
          <span className="muted">
            最終 {new Date(member.agent_last_seen_at).toLocaleString('ja-JP')}
          </span>
        )}
        {!member.settings.agent_may_post && <span className="pill warn">AI発言オフ</span>}
        {!member.settings.agent_may_read && <span className="pill warn">AI読取オフ</span>}
        {member.settings.auto_publish_summaries && (
          <span className="pill decision">要約を自動公開</span>
        )}
      </div>

      <div className="grid2" style={{ marginTop: 10 }}>
        <div>
          <label className="muted">権限</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            disabled={!canEdit}
            style={{ width: 'auto' }}
          >
            {(Object.keys(ROLE_LABEL) as Role[]).map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
          <div className="muted" style={{ marginTop: 4 }}>
            {ROLE_HELP[role]}
          </div>
        </div>

        <div>
          <label className="muted">責任領域（最終判断をする領域）</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4 }}>
            {ALL_AUTHORITIES.map((a) => (
              <label key={a} className="row" style={{ gap: 6, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={authorities.includes(a)}
                  disabled={!canEdit}
                  onChange={() => toggle(a)}
                  style={{ width: 'auto' }}
                />
                <span>
                  <strong>{AUTHORITY_LABEL[a]}</strong>{' '}
                  <span className="muted">{AUTHORITY_DESCRIPTION[a]}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>

      {canEdit && (
        <div className="row" style={{ marginTop: 10, justifyContent: 'flex-end' }}>
          {confirming ? (
            <>
              <span className="muted">
                {member.display_name} をこのプロジェクトから削除します。
                他に所属が無ければトークンも失効します。発言の履歴は残ります。
              </span>
              <button onClick={() => setConfirming(false)}>やめる</button>
              <button className="danger" disabled={busy} onClick={onRemove}>
                削除する
              </button>
            </>
          ) : (
            <>
              <button className="danger" disabled={busy} onClick={() => setConfirming(true)}>
                削除
              </button>
              <button
                className="primary"
                disabled={busy || !dirty}
                onClick={() => onSave({ role, authorities })}
              >
                変更を保存
              </button>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function InviteCard({
  room,
  invites,
  busy,
  onCreate,
  onRevoke,
}: {
  room: string
  invites: Invite[]
  busy: boolean
  onCreate: (input: {
    display_name: string
    org: string
    role: Role
    authorities: Authority[]
  }) => void
  onRevoke: (code: string) => void
}) {
  const [name, setName] = useState('')
  const [org, setOrg] = useState('')
  const [role, setRole] = useState<Role>('member')
  const [authorities, setAuthorities] = useState<Authority[]>([])

  const pending = invites.filter((i) => !i.redeemed_at && !i.revoked_at)
  const past = invites.filter((i) => i.redeemed_at || i.revoked_at)

  const toggle = (a: Authority) =>
    setAuthorities((prev) => (prev.includes(a) ? prev.filter((x) => x !== a) : [...prev, a]))

  return (
    <div className="card">
      <h2>招待</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        責任領域をここで指定しておくと、相手は参加した時点から質問が届く状態になります。
        既にトークンを持っている人が引き換えた場合は、そのまま同じアカウントに
        このプロジェクトが追加されます。
      </p>

      <div className="grid2">
        <div>
          <label className="muted">表示名</label>
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="八十島さん" />
          <div style={{ height: 8 }} />
          <label className="muted">会社</label>
          <input value={org} onChange={(e) => setOrg(e.target.value)} placeholder="営業会社" />
          <div style={{ height: 8 }} />
          <label className="muted">権限</label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as Role)}
            style={{ width: 'auto' }}
          >
            {(Object.keys(ROLE_LABEL) as Role[]).map((r) => (
              <option key={r} value={r}>
                {ROLE_LABEL[r]}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="muted">責任領域</label>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, marginTop: 4 }}>
            {ALL_AUTHORITIES.map((a) => (
              <label key={a} className="row" style={{ gap: 6, fontSize: 13 }}>
                <input
                  type="checkbox"
                  checked={authorities.includes(a)}
                  onChange={() => toggle(a)}
                  style={{ width: 'auto' }}
                />
                <span>
                  <strong>{AUTHORITY_LABEL[a]}</strong>{' '}
                  <span className="muted">{AUTHORITY_DESCRIPTION[a]}</span>
                </span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <div className="row" style={{ marginTop: 10, justifyContent: 'flex-end' }}>
        <button
          className="primary"
          disabled={busy || !name.trim() || !org.trim()}
          onClick={() => {
            onCreate({ display_name: name.trim(), org: org.trim(), role, authorities })
            setName('')
            setOrg('')
            setAuthorities([])
          }}
        >
          招待コードを発行
        </button>
      </div>

      {pending.length > 0 && (
        <>
          <h3 style={{ marginTop: 16 }}>未使用の招待 {pending.length} 件</h3>
          {pending.map((i) => (
            <div key={i.code} className="row" style={{ marginBottom: 6 }}>
              <code style={{ fontSize: 15 }}>{i.code}</code>
              <span className="muted">
                {i.display_name} ({i.org}) / {ROLE_LABEL[i.role]}
                {i.authorities.length > 0 &&
                  ` / ${i.authorities.map((a) => AUTHORITY_LABEL[a]).join('・')}`}
              </span>
              <span className="muted">
                期限 {new Date(i.expires_at).toLocaleString('ja-JP')}
              </span>
              <button
                style={{ marginLeft: 'auto' }}
                onClick={() => navigator.clipboard?.writeText(i.url)}
              >
                リンクをコピー
              </button>
              <button className="danger" disabled={busy} onClick={() => onRevoke(i.code)}>
                取り消す
              </button>
            </div>
          ))}
        </>
      )}

      {past.length > 0 && (
        <>
          <h3 style={{ marginTop: 16 }}>過去の招待</h3>
          {past.slice(0, 10).map((i) => (
            <div key={i.code} className="muted" style={{ fontSize: 13 }}>
              {i.code} · {i.display_name} ({i.org}) ·{' '}
              {i.redeemed_at ? `使用済み ${new Date(i.redeemed_at).toLocaleDateString('ja-JP')}` : '取り消し済み'}
            </div>
          ))}
        </>
      )}
    </div>
  )
}


/**
 * A person's own choices about their Claude. Separate from the member row above
 * because these are never someone else's to change: an owner sets roles and areas,
 * but may not silence or unleash another person's agent.
 */
function MySettings({
  room,
  me,
  busy,
  onSave,
}: {
  room: string
  me: Member
  busy: boolean
  onSave: (patch: { settings?: Partial<MemberSettings>; agent_nickname?: string | null }) => void
}) {
  const [nickname, setNickname] = useState(me.agent_nickname ?? '')

  useEffect(() => setNickname(me.agent_nickname ?? ''), [me.agent_nickname])

  const toggle = (key: keyof MemberSettings) =>
    onSave({ settings: { [key]: !me.settings[key] } })

  const label = nickname.trim()
    ? agentLabel(me.display_name, nickname.trim())
    : agentLabel(me.display_name, null)

  return (
    <div className="card">
      <h2>自分の AI の設定</h2>
      <p className="muted" style={{ marginTop: 0 }}>
        ここはあなた専用です。オーナーでも他の人のこの設定は変更できません。
      </p>

      <label className="muted">AI の呼び名（任意）</label>
      <div className="row">
        <input
          value={nickname}
          onChange={(e) => setNickname(e.target.value)}
          placeholder="アルフレッド"
          style={{ maxWidth: 240 }}
        />
        <button
          disabled={busy || nickname.trim() === (me.agent_nickname ?? '')}
          onClick={() => onSave({ agent_nickname: nickname.trim() || null })}
        >
          保存
        </button>
        <span className="muted">表示: {label}</span>
      </div>
      <p className="muted" style={{ fontSize: 12 }}>
        呼び名を付けても、誰の AI かは必ず併記されます。会社をまたぐ場では
        「誰が言ったか」が曖昧になってはいけないためです。
      </p>

      <h3 style={{ marginTop: 16 }}>AI に何をさせるか</h3>
      {(
        [
          ['agent_may_post', 'AI の発言を許可する', 'オフにすると AI は下書きしか作れません。発言は自分で行います'],
          ['agent_may_read', 'AI にこのチャットを読ませる', 'オフにすると自動読み込みを止めます。明示的に指示したときだけ読みます'],
          ['auto_publish_summaries', 'セッション要約を承認なしで公開する', '既定はオフ。オンにすると相手に自動で流れます（認証情報の検出は引き続き効きます）'],
          ['auto_reply_when_away', '不在時に自動で受付返信する', 'あなたの AI がオフラインのとき、公開済みの情報だけを返します'],
        ] as Array<[keyof MemberSettings, string, string]>
      ).map(([key, title, help]) => (
        <label
          key={key}
          className="row"
          style={{ gap: 8, alignItems: 'flex-start', marginBottom: 8, fontSize: 13 }}
        >
          <input
            type="checkbox"
            checked={me.settings[key]}
            disabled={busy}
            onChange={() => toggle(key)}
            style={{ width: 'auto', marginTop: 3 }}
          />
          <span>
            <strong>{title}</strong>
            <br />
            <span className="muted">{help}</span>
          </span>
        </label>
      ))}
    </div>
  )
}


/**
 * Renaming the project.
 *
 * Owners only, and in the members tab rather than in the project switcher: this is
 * administration, and the switcher is for getting somewhere. The name is what both
 * companies see everywhere the project is mentioned, so it says so plainly.
 */
function RenameCard({
  room,
  current,
  onDone,
}: {
  room: string
  current: string
  onDone: () => void
}) {
  const [name, setName] = useState(current)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [done, setDone] = useState(false)

  useEffect(() => {
    setName(current)
  }, [current])

  const save = async () => {
    setBusy(true)
    setError(null)
    try {
      await api.renameRoom(room, name.trim())
      setDone(true)
      onDone()
    } catch (err) {
      setError(describeError(err))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="card">
      <h2>プロジェクト名</h2>
      {error && <div className="error">{error}</div>}
      {done && <div className="notice">プロジェクト名を変更しました。</div>}
      <p className="muted" style={{ marginTop: 0 }}>
        相手の会社にも、この名前で表示されます。変更しても履歴・メンバー・招待リンクは
        そのままです。
      </p>
      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
      <div className="row" style={{ marginTop: 8, justifyContent: 'flex-end' }}>
        <button
          className="primary"
          disabled={busy || !name.trim() || name.trim() === current}
          onClick={save}
        >
          名前を変更する
        </button>
      </div>
    </div>
  )
}
